import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { lighten, TextField, Typography } from '@mui/material';
import _ from 'lodash';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

interface Props {
  value: string | string[];
  variant?: 'background' | 'white';
  color?: string;
  label?: string;
  multiple?: boolean;
  disabled?: boolean;
  dense?: boolean;
  style?: React.CSSProperties;
  error?: boolean;
  errorText?: string;
  children?: React.ReactNode;
  /** @deprecated Legacy prop – kept for backward compat */
  disableBorder?: boolean;
  /** @deprecated Legacy prop – kept for backward compat */
  backgroundColor?: string;

  onChange(event: React.ChangeEvent<HTMLInputElement>): void;

  onOpen?(): void;
}

const PaiSelect: React.FC<Props> = ({
  value,
  variant = 'white',
  color,
  label,
  multiple,
  style,
  disabled,
  dense,
  error,
  errorText,
  onChange,
  onOpen,
  children,
}) => {
  const { classes } = useStyles({ variant, color, dense, disabled, error });

  return (
    <div style={{ width: style?.width || '100%', maxWidth: style?.maxWidth || undefined }}>
      {!_.isNil(label) ? <Typography className={classes.label}>{label}</Typography> : null}
      <TextField
        className={classes.textfield}
        InputProps={{ disableUnderline: true, classes: { input: classes.fieldInput } }}
        SelectProps={{
          displayEmpty: true,
          IconComponent: ExpandMoreIcon,
          MenuProps: { classes: { list: classes.menuList, paper: classes.menuPaper }, disableAutoFocusItem: true },
          multiple: multiple,
          onOpen: onOpen,
          autoFocus: false,
        }}
        select={true}
        variant={'standard'}
        fullWidth={true}
        value={value}
        onChange={onChange}
        style={style}
        disabled={disabled}
        error={!!error}
      >
        {children}
      </TextField>
      {!!error && !_.isNil(errorText) ? <Typography className={classes.errorText}>{errorText}</Typography> : null}
    </div>
  );
};

const useStyles = makeStyles<{
  variant?: 'background' | 'white';
  color?: string;
  dense?: boolean;
  disabled?: boolean;
  error?: boolean;
}>()((theme, { variant, color, dense, disabled, error }) => ({
  textfield: {
    border:
      variant !== 'background'
        ? !error
          ? disabled
            ? `1px solid ${theme.palette.action.disabled}`
            : `1px solid ${theme.palette.primary.main}`
          : `1px solid ${theme.palette.error.main}`
        : undefined,
    borderRadius: '10px',
    '& .MuiSvgIcon-root': {
      color: disabled ? theme.palette.greys.medium : theme.palette.primary.main,
      height: '100%',
      top: 0,
      right: '8px',
      background: !error ? (variant === 'background' ? theme.palette.backgrounds.main_new : '#FFF') : lighten(theme.palette.error.main, 0.9),
    },
  },
  fieldInput: {
    color: color,
    background: !error ? (variant === 'background' ? theme.palette.backgrounds.main_new : '#FFF') : lighten(theme.palette.error.main, 0.9),
    fontSize: dense ? theme.typography.pxToRem(13) : theme.typography.pxToRem(15),
    padding: !dense ? '8px 12px' : '0px 8px',
    paddingRight: '40px !important',
    borderRadius: '10px',
    '&:focus': {
      borderRadius: '10px',
      background: !error ? (variant === 'background' ? theme.palette.backgrounds.main_new : '#FFF') : lighten(theme.palette.error.main, 0.9),
    },
  },
  menuPaper: {
    borderRadius: '6px',
    maxHeight: '33vh',
  },
  menuList: {
    padding: 0,
    '& .MuiMenuItem-root': {
      fontSize: theme.typography.pxToRem(14),
    },
  },
  label: {
    marginBottom: '4px',
    marginLeft: '4px',
    fontSize: theme.typography.pxToRem(14),
  },
  errorText: {
    fontSize: theme.typography.pxToRem(10),
    marginTop: '4px',
    textAlign: 'right',
    color: 'red',
    padding: '0 4px',
  },
}));

export default PaiSelect;
